import { Button } from "components/ui";
import { InfoTooltip } from "components/ui/InfoTooltip/InfoTooltip";
import {
  buildSlotDetail,
  formatCourseName,
  formatOwnershipNote,
  formatRepeatReason,
  formatSlotDates,
  isRecordableSlot,
} from "features/immunizations/utils/schedule";

import s from "./ScheduleGroup.module.css";

const ScheduleSlot = ({ slot, onRecord }) => {
  const ownershipNote = formatOwnershipNote(slot);
  const dates = formatSlotDates(slot);
  const detail = buildSlotDetail(slot);
  const repeatReason = formatRepeatReason(slot);

  return (
    <li className={s.slot}>
      <div className={s.slotHeader}>
        <span className={s.slotName}>
          {formatCourseName(slot)} · Dose {slot.doseNumber}
        </span>
        <span className={s.slotMeta}>
          <span className={s.slotVisit}>{slot.visitLabel}</span>
          {/* The upper age bound and the schedule's clinical notes live here rather than
              on the row. They are reference material — needed when someone stops to ask,
              not while scanning for what is outstanding. */}
          {!!detail.length && (
            <InfoTooltip
              text={detail}
              ariaLabel={`Details for ${formatCourseName(slot)} dose ${slot.doseNumber}`}
            />
          )}
        </span>
      </div>

      {!!dates.length && (
        <div className={s.slotDates}>
          {dates.map((date) => (
            <span key={date}>{date}</span>
          ))}
        </div>
      )}

      {/* Why this dose is being asked for again (KIN-1923). On the row and not behind the
          information icon, because without it the two screens contradict each other: the dose
          list shows the dose as given and this shows it as outstanding. It is also not an
          error — the dose was given, reported and claimed; it simply does not fill the slot. */}
      {!!repeatReason && <p className={s.slotRepeatReason}>{repeatReason}</p>}

      {/* Stays on the row, unlike the notes: it changes who acts, not merely what they
          should know. */}
      {!!ownershipNote && <p className={s.slotOwnership}>{ownershipNote}</p>}

      {/* Only on a dose that can actually be given now, and only where it is ours to
          give. Opens the ordinary Add vaccine form with the dose number filled in, and
          the product too where the course has exactly one. */}
      {!!isRecordableSlot(slot) && (
        <div className={s.slotAction}>
          {/* Outline, not primary: there is one of these per outstanding dose, and they
              must not out-shout the single "Add vaccine" at the top of the page. */}
          <Button variant="outline" size="small" onClick={() => onRecord(slot)}>
            Record this dose
          </Button>
        </div>
      )}
    </li>
  );
};

// The title and count live on the tab now, so the group repeats neither. The caption
// stays: "Overdue" is not self-explanatory when the record may be incomplete, and
// "Waiting on an earlier dose" needs to say what it is waiting for.
export const ScheduleGroup = ({ group, onRecord }) => (
  <section className={s.group} data-testid={`schedule-group-${group.id}`}>
    {!!group.caption && <p className={s.groupCaption}>{group.caption}</p>}
    <ul className={s.slotList}>
      {group.slots.map((slot) => (
        <ScheduleSlot
          key={`${slot.courseKey}-${slot.doseNumber}`}
          slot={slot}
          onRecord={onRecord}
        />
      ))}
    </ul>
  </section>
);
